// The curated connection catalog: the reviewed subset of each provider's tools Parcel will ever
// offer, and the policy each one carries. An unclassified tool can never reach a model.
import type { SpecToolDecision } from '@parcel/types';
import { CONNECTION_SCHEMAS } from './connection-schemas.js';
import { fnv1a64, stableStringify } from './native-registry.js';
import { floorForEffect } from './policy.js';
import { CONNECTION_CATEGORIES } from './types.js';
import type {
  ConnectionCatalogEntry,
  ConnectionCategory,
  ConnectionScopingMode,
  CuratedConnectionTool,
  NativeToolEffect,
  NativeToolIdempotency,
} from './types.js';

/** Everything about a curated tool except the wire slug that keys it and its derived version. */
export interface CuratedConnectionToolMetadata {
  key: string;
  description: string;
  effect: NativeToolEffect;
  defaultPolicy: SpecToolDecision;
  registryFloor: SpecToolDecision;
  idempotency: NativeToolIdempotency;
  /** Only ever sourced from a captured MCP tools/list response, never the REST catalog fixture. */
  inputSchemaFingerprint?: string;
}

export interface CuratedConnectionEntryInput {
  key: string;
  name: string;
  description: string;
  icon: string;
  categories: readonly ConnectionCategory[];
  providerToolkit: string;
  scopingModes: readonly ConnectionScopingMode[];
  sharedPolicyFloor: SpecToolDecision;
  requiredScopes: readonly string[];
  accessNote?: string;
  /** Provider slugs this entry advertises, in display order. */
  toolSlugs: readonly string[];
  /** Classification for every slug above, keyed by provider slug. */
  toolMetadata: Readonly<Record<string, CuratedConnectionToolMetadata>>;
}

// Stable stand-in for "no MCP capture yet" so adding a real fingerprint later moves the version.
const NO_CAPTURED_INPUT_SCHEMA = 'no-input-schema-captured';

// Immutable per-tool version; excludes description so a wording edit cannot invalidate an approval.
export function connectionToolContractVersion(
  tool: Omit<CuratedConnectionTool, 'version'>,
): string {
  const content = stableStringify({
    key: tool.key,
    providerSlug: tool.providerSlug,
    effect: tool.effect,
    defaultPolicy: tool.defaultPolicy,
    registryFloor: tool.registryFloor,
    idempotency: tool.idempotency,
    inputSchemaFingerprint: tool.inputSchemaFingerprint ?? NO_CAPTURED_INPUT_SCHEMA,
  });
  return `v1.${fnv1a64(content).slice(0, 12)}`;
}

// Hashes ONLY inputSchema, never the whole wire tool object: annotations and execution churn
// on every capture and would otherwise read as drift.
export function fingerprintInputSchema(schema: Record<string, unknown>): string {
  return `v1.${fnv1a64(stableStringify(schema)).slice(0, 12)}`;
}

// Excludes display copy, category, and accessNote on purpose: none of those need re-consent, but
// a requiredScopes or tool-version change does. See ConnectionCatalogEntry.version.
function connectionCatalogEntryVersion(entry: Omit<ConnectionCatalogEntry, 'version'>): string {
  const content = stableStringify({
    key: entry.key,
    provider: entry.provider,
    providerToolkit: entry.providerToolkit,
    scopingModes: entry.scopingModes,
    sharedPolicyFloor: entry.sharedPolicyFloor,
    requiredScopes: entry.requiredScopes,
    toolVersions: entry.tools.map((tool) => tool.version),
  });
  return `v1.${fnv1a64(content).slice(0, 12)}`;
}

// Mirrors buildRegistry(): an advertised slug with no classification, or a classified slug
// nobody advertises, throws at build time rather than shipping silently.
export function buildCatalogEntry(input: CuratedConnectionEntryInput): ConnectionCatalogEntry {
  // Same contract as the tool-slug checks below: a miscategorised entry throws at module load
  // rather than rendering an entry the connectors filter can never surface.
  if (input.categories.length === 0) {
    throw new Error(`Connection "${input.key}" declares no category. Give it at least one.`);
  }
  if (new Set(input.categories).size !== input.categories.length) {
    throw new Error(`Connection "${input.key}" repeats a category.`);
  }
  for (const category of input.categories) {
    if (!CONNECTION_CATEGORIES.includes(category)) {
      throw new Error(`Connection "${input.key}" uses unknown category "${category}".`);
    }
  }
  const classified = new Set(Object.keys(input.toolMetadata));
  const tools: CuratedConnectionTool[] = input.toolSlugs.map((providerSlug) => {
    const metadata = input.toolMetadata[providerSlug];
    if (!metadata) {
      throw new Error(
        `Connection "${input.key}" advertises "${providerSlug}" with no classification. Add it to the metadata map before advertising it.`,
      );
    }
    classified.delete(providerSlug);
    // Only source of inputSchema/inputSchemaFingerprint: absent when Task 1 capture found nothing.
    const captured = CONNECTION_SCHEMAS[input.key]?.[providerSlug];
    const partial: Omit<CuratedConnectionTool, 'version'> = {
      ...metadata,
      providerSlug,
      ...captured,
    };
    return { ...partial, version: connectionToolContractVersion(partial) };
  });

  if (classified.size > 0) {
    throw new Error(
      `Connection "${input.key}" classifies tools that are not advertised: ${[...classified].join(', ')}.`,
    );
  }

  const partialEntry: Omit<ConnectionCatalogEntry, 'version'> = {
    key: input.key,
    name: input.name,
    description: input.description,
    icon: input.icon,
    categories: input.categories,
    provider: 'composio',
    providerToolkit: input.providerToolkit,
    scopingModes: input.scopingModes,
    sharedPolicyFloor: input.sharedPolicyFloor,
    requiredScopes: input.requiredScopes,
    accessNote: input.accessNote,
    tools,
  };
  return { ...partialEntry, version: connectionCatalogEntryVersion(partialEntry) };
}

// -- Custom connections --

/** Slugs Composio retired but captured descriptions still name; rendered as (not available). */
export const RETIRED_PROVIDER_SLUGS: readonly string[] = ['SALESFORCE_GET_ALL_FIELDS_FOR_OBJECT'];

export const CUSTOM_CATALOG_KEY_PREFIX = 'custom:';

/** Setting accessNote is what routes a connector through the connect confirmation dialog. */
export const CUSTOM_CONNECTION_ACCESS_NOTE =
  'Parcel does not control which tools this server makes available, and cannot verify that they will work as intended or that they will not change.';

export function isCustomCatalogKey(key: string): boolean {
  return key.startsWith(CUSTOM_CATALOG_KEY_PREFIX);
}

/** `custom:<uuid>`: unique per connection, so spec.connections' partial unique index on
    (workspace, catalog key) permits many custom servers per workspace. */
export function newCustomCatalogKey(): string {
  return `${CUSTOM_CATALOG_KEY_PREFIX}${crypto.randomUUID()}`;
}

export interface CustomConnectionToolInput {
  providerSlug: string;
  toolKey: string;
  description: string;
  inputSchema: Record<string, unknown>;
  inputSchemaFingerprint: string;
  /** Null until an admin classifies it. */
  effect: NativeToolEffect | null;
  /** The fingerprint that was classified; drift when it differs from inputSchemaFingerprint. */
  classifiedFingerprint: string | null;
}

export interface CustomCatalogEntryInput {
  catalogKey: string;
  name: string;
  tools: readonly CustomConnectionToolInput[];
}

function buildCustomTool(input: CustomConnectionToolInput): CuratedConnectionTool {
  const shared = {
    key: input.toolKey,
    providerSlug: input.providerSlug,
    description: input.description,
    inputSchema: input.inputSchema,
    inputSchemaFingerprint: input.inputSchemaFingerprint,
  };
  // A classification only counts for the exact fingerprint it was given; a moved contract re-blocks.
  const effect = input.classifiedFingerprint === input.inputSchemaFingerprint ? input.effect : null;
  const partial: Omit<CuratedConnectionTool, 'version'> = effect
    ? {
        ...shared,
        effect,
        registryFloor: floorForEffect(effect),
        defaultPolicy: floorForEffect(effect),
        idempotency: effect === 'read' ? 'safe_retry' : 'never_retry_unknown',
      }
    : {
        ...shared,
        unreviewed: true,
        // Not a claim about what the tool does: it is the strictest class, and an unclassified
        // tool must fail to the strictest.
        effect: 'admin_security',
        registryFloor: 'blocked',
        defaultPolicy: 'blocked',
        idempotency: 'never_retry_unknown',
      };
  return { ...partial, version: connectionToolContractVersion(partial) };
}

/** The catalog entry for one custom MCP server, synthesized from what it advertises plus the
    classification an admin gave each tool. Parcel curates nothing here, hence no category. */
export function buildCustomCatalogEntry(input: CustomCatalogEntryInput): ConnectionCatalogEntry {
  const partialEntry: Omit<ConnectionCatalogEntry, 'version'> = {
    key: input.catalogKey,
    name: input.name,
    description: 'A custom MCP server this workspace connected.',
    icon: 'custom',
    categories: [],
    provider: 'custom',
    providerToolkit: 'custom',
    scopingModes: ['workspace'],
    sharedPolicyFloor: 'needs_approval',
    requiredScopes: [],
    accessNote: CUSTOM_CONNECTION_ACCESS_NOTE,
    tools: input.tools.map(buildCustomTool),
  };
  return { ...partialEntry, version: connectionCatalogEntryVersion(partialEntry) };
}

// HubSpot: 10 reads, 6 reversible writes. Archive, merge, recycle, GDPR-delete, and batch tools
// are excluded: merges and deletes are irreversible, batches widen blast radius for no gain.

const HUBSPOT_TOOL_SLUGS = [
  'HUBSPOT_SEARCH_CONTACTS_BY_CRITERIA',
  'HUBSPOT_READ_CRM_CONTACT_BY_ID',
  'HUBSPOT_HUBSPOT_LIST_CONTACTS',
  'HUBSPOT_CREATE_CONTACT',
  'HUBSPOT_HUBSPOT_UPDATE_CONTACT',
  'HUBSPOT_HUBSPOT_SEARCH_COMPANIES',
  'HUBSPOT_HUBSPOT_GET_COMPANY',
  'HUBSPOT_HUBSPOT_LIST_COMPANIES',
  'HUBSPOT_CREATE_COMPANY',
  'HUBSPOT_HUBSPOT_UPDATE_COMPANY',
  'HUBSPOT_SEARCH_DEALS_BY_CRITERIA',
  'HUBSPOT_HUBSPOT_GET_DEAL',
  'HUBSPOT_HUBSPOT_LIST_DEALS',
  'HUBSPOT_CREATE_DEAL',
  'HUBSPOT_HUBSPOT_UPDATE_DEAL',
  'HUBSPOT_RETRIEVE_OWNERS',
] as const;

// Descriptions are taken verbatim from the captured fixture, not invented. needs_approval on
// every write also guards HubSpot workflow side effects a bare record-level undo cannot reach.
const HUBSPOT_TOOL_METADATA: Readonly<Record<string, CuratedConnectionToolMetadata>> = {
  HUBSPOT_SEARCH_CONTACTS_BY_CRITERIA: {
    key: 'contact.search',
    description:
      'Searches for hubspot contacts using a text query, specific filter criteria (filters in a group are anded, groups are ored), sorting, and pagination to retrieve selected properties.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_READ_CRM_CONTACT_BY_ID: {
    key: 'contact.get',
    description:
      'Retrieves a hubspot crm contact by its unique internal hubspot id, allowing selection of properties, historical values, associated objects, and filtering for archived contacts.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_HUBSPOT_LIST_CONTACTS: {
    key: 'contact.list',
    description: 'Retrieves a paginated list of hubspot contacts.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_CREATE_CONTACT: {
    key: 'contact.create',
    description: 'Creates a new hubspot contact.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    // Retrying a blind create risks a duplicate contact; only safe behind our own key.
    idempotency: 'keyed',
  },
  HUBSPOT_HUBSPOT_UPDATE_CONTACT: {
    key: 'contact.update',
    description: 'Updates properties for an existing hubspot contact.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  HUBSPOT_HUBSPOT_SEARCH_COMPANIES: {
    key: 'company.search',
    description: 'Searches for hubspot companies using flexible criteria and filters.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_HUBSPOT_GET_COMPANY: {
    key: 'company.get',
    description: 'Retrieves a hubspot company by its id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_HUBSPOT_LIST_COMPANIES: {
    key: 'company.list',
    description: 'Retrieves a paginated list of hubspot companies.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_CREATE_COMPANY: {
    key: 'company.create',
    description: 'Creates a new hubspot company.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  HUBSPOT_HUBSPOT_UPDATE_COMPANY: {
    key: 'company.update',
    description: 'Updates properties for an existing hubspot company.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  HUBSPOT_SEARCH_DEALS_BY_CRITERIA: {
    key: 'deal.search',
    description:
      'Searches hubspot deals using complex criteria (text queries, property filters, sorting, pagination) for targeted retrieval beyond simple id lookups; ensure filter operator/value combinations are valid for the target hubspot property.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_HUBSPOT_GET_DEAL: {
    key: 'deal.get',
    description: 'Retrieves a hubspot deal by its id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_HUBSPOT_LIST_DEALS: {
    key: 'deal.list',
    description: 'Retrieves a paginated list of hubspot deals.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  HUBSPOT_CREATE_DEAL: {
    key: 'deal.create',
    description: 'Creates a new hubspot deal.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  HUBSPOT_HUBSPOT_UPDATE_DEAL: {
    key: 'deal.update',
    description: 'Updates properties for an existing hubspot deal.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  HUBSPOT_RETRIEVE_OWNERS: {
    key: 'owner.list',
    description:
      'Retrieves a list of all owners in the hubspot crm, including their id, first name, last name, email, and user id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
};

// Derived from actual per-domain needs, not summed from each tool's own `scopes` field: several
// curated read/create slugs report an empty scopes array despite needing read or write access.
const HUBSPOT_REQUIRED_SCOPES = [
  'crm.objects.contacts.read',
  'crm.objects.contacts.write',
  'crm.objects.companies.read',
  'crm.objects.companies.write',
  'crm.objects.deals.read',
  'crm.objects.deals.write',
  'crm.objects.owners.read',
] as const;

// Teams: 10 reads, 5 reversible writes. Provisioning, archive, delete, and template tools are
// excluded, along with user.list and team_member.list: their only registered scopes write.
const MICROSOFT_TEAMS_TOOL_SLUGS = [
  'MICROSOFT_TEAMS_TEAMS_LIST_PEOPLE',
  'MICROSOFT_TEAMS_TEAMS_LIST',
  'MICROSOFT_TEAMS_GET_TEAM',
  'MICROSOFT_TEAMS_TEAMS_LIST_CHANNELS',
  'MICROSOFT_TEAMS_GET_CHANNEL',
  'MICROSOFT_TEAMS_CHATS_GET_ALL_CHATS',
  'MICROSOFT_TEAMS_TEAMS_LIST_CHAT_MESSAGES',
  'MICROSOFT_TEAMS_GET_CHAT_MESSAGE',
  'MICROSOFT_TEAMS_TEAMS_GET_MESSAGE',
  'MICROSOFT_TEAMS_LIST_MESSAGE_REPLIES',
  'MICROSOFT_TEAMS_TEAMS_POST_CHANNEL_MESSAGE',
  'MICROSOFT_TEAMS_TEAMS_POST_CHAT_MESSAGE',
  'MICROSOFT_TEAMS_TEAMS_POST_MESSAGE_REPLY',
  'MICROSOFT_TEAMS_UPDATE_CHANNEL_MESSAGE',
  'MICROSOFT_TEAMS_UPDATE_CHAT_MESSAGE',
] as const;

// Descriptions are taken verbatim from the captured fixture, not invented. No tool here carries
// an inputSchemaFingerprint (Ruling 1): the REST fixture schema is unverified against MCP.
const MICROSOFT_TEAMS_TOOL_METADATA: Readonly<Record<string, CuratedConnectionToolMetadata>> = {
  MICROSOFT_TEAMS_TEAMS_LIST_PEOPLE: {
    key: 'person.list',
    description:
      "Retrieves a list of people relevant to a specified user from microsoft graph, noting the `search` parameter is only effective if `user id` is 'me'.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_TEAMS_LIST: {
    key: 'team.list',
    description:
      'Retrieves microsoft teams accessible by the authenticated user, allowing filtering, property selection, and pagination.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_GET_TEAM: {
    key: 'team.get',
    description: 'Tool to get a specific team. use when full details of one team by id are needed.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_TEAMS_LIST_CHANNELS: {
    key: 'channel.list',
    description:
      'Retrieves channels for a specified microsoft teams team id (must be valid and for an existing team), with options to include shared channels, filter results, and select properties.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_GET_CHANNEL: {
    key: 'channel.get',
    description:
      'Tool to get a specific channel in a team. use after obtaining valid team and channel ids to fetch channel details.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_CHATS_GET_ALL_CHATS: {
    key: 'chat.list',
    description:
      'Retrieves all microsoft teams chats a specified user is part of, supporting filtering, property selection, and pagination.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_TEAMS_LIST_CHAT_MESSAGES: {
    key: 'chat_message.list',
    description:
      'Retrieves messages (newest first) from an existing and accessible microsoft teams one-on-one chat, group chat, or channel thread, specified by `chat id`.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_GET_CHAT_MESSAGE: {
    key: 'chat_message.get',
    description:
      'Tool to get a specific chat message. use after confirming chat id and message id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_TEAMS_GET_MESSAGE: {
    key: 'channel_message.get',
    description:
      'Retrieves a specific message from a microsoft teams channel using its team, channel, and message ids.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_LIST_MESSAGE_REPLIES: {
    key: 'channel_message_reply.list',
    description:
      'Tool to list replies to a channel message. use after obtaining team, channel, and message ids.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  MICROSOFT_TEAMS_TEAMS_POST_CHANNEL_MESSAGE: {
    key: 'channel_message.send',
    description:
      'Posts a new text or html message to a specified channel in a microsoft teams team.',
    // A posted channel message cannot be unsent, only edited.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
  MICROSOFT_TEAMS_TEAMS_POST_CHAT_MESSAGE: {
    key: 'chat_message.send',
    description:
      "Sends a non-empty message (text or html) to a specified, existing microsoft teams chat; content must be valid html if `content type` is 'html'.",
    // A posted chat message cannot be unsent, only edited.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
  MICROSOFT_TEAMS_TEAMS_POST_MESSAGE_REPLY: {
    key: 'channel_message_reply.send',
    description:
      'Sends a reply to an existing message, identified by `message id`, within a specific `channel id` of a given `team id` in microsoft teams.',
    // A posted reply cannot be unsent, only edited.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
  MICROSOFT_TEAMS_UPDATE_CHANNEL_MESSAGE: {
    key: 'channel_message.update',
    description:
      'Tool to update a message in a channel. use when you need to modify an existing channel message after confirming channel and message ids.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  MICROSOFT_TEAMS_UPDATE_CHAT_MESSAGE: {
    key: 'chat_message.update',
    description:
      'Tool to update a specific message in a chat. use when you need to correct or modify a sent chat message.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
};

// Verified against toolkit-microsoft_teams.json's registered scopes, not each tool's own field
// (Ruling 5); User.Read is Microsoft's non-removable sign-in baseline for a delegated Graph app.
const MICROSOFT_TEAMS_REQUIRED_SCOPES = [
  'User.Read',
  'Team.ReadBasic.All',
  'Channel.ReadBasic.All',
  'People.Read.All',
  'ChannelMessage.Read.All',
  'ChannelMessage.Send',
  'ChannelMessage.ReadWrite',
  'Chat.ReadWrite',
  'offline_access',
] as const;

// Outlook: 8 reads, 8 reversible writes. Contact/folder/rule/category admin tools, mailbox
// settings, and delete tools are excluded: they configure the mailbox, not a contact's record.
const OUTLOOK_TOOL_SLUGS = [
  'OUTLOOK_OUTLOOK_GET_CONTACT',
  'OUTLOOK_OUTLOOK_LIST_CONTACTS',
  'OUTLOOK_OUTLOOK_GET_MESSAGE',
  'OUTLOOK_OUTLOOK_LIST_MESSAGES',
  'OUTLOOK_OUTLOOK_SEARCH_MESSAGES',
  'OUTLOOK_OUTLOOK_GET_EVENT',
  'OUTLOOK_OUTLOOK_LIST_EVENTS',
  'OUTLOOK_OUTLOOK_GET_SCHEDULE',
  'OUTLOOK_OUTLOOK_CREATE_CONTACT',
  'OUTLOOK_OUTLOOK_UPDATE_CONTACT',
  'OUTLOOK_OUTLOOK_SEND_EMAIL',
  'OUTLOOK_OUTLOOK_REPLY_EMAIL',
  'OUTLOOK_OUTLOOK_CREATE_DRAFT',
  'OUTLOOK_OUTLOOK_CREATE_DRAFT_REPLY',
  'OUTLOOK_OUTLOOK_CALENDAR_CREATE_EVENT',
  'OUTLOOK_OUTLOOK_UPDATE_CALENDAR_EVENT',
] as const;

// Descriptions are taken verbatim from the captured fixture, not invented. Send and reply are
// real sends, not undoable; the draft variants are ordinary reversible writes.
const OUTLOOK_TOOL_METADATA: Readonly<Record<string, CuratedConnectionToolMetadata>> = {
  OUTLOOK_OUTLOOK_GET_CONTACT: {
    key: 'contact.get',
    description:
      "Retrieves a specific outlook contact by its `contact id` from the contacts of a specified `user id` (defaults to 'me' for the authenticated user).",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_LIST_CONTACTS: {
    key: 'contact.list',
    description:
      "Retrieves a user's microsoft outlook contacts, from the default or a specified contact folder.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_GET_MESSAGE: {
    key: 'message.get',
    description:
      "Retrieves a specific email message by its id from the specified user's outlook mailbox. use the 'select' parameter to include specific fields like 'internetmessageheaders' for filtering automated emails.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_LIST_MESSAGES: {
    key: 'message.list',
    description:
      "Retrieves a list of email messages from a specified mail folder in an outlook mailbox, with options for filtering (including by conversationid to get all messages in a thread), pagination, and sorting; ensure 'user id' and 'folder' are valid, and all date/time strings are in iso 8601 format.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_SEARCH_MESSAGES: {
    key: 'message.search',
    description:
      'Searches messages in a microsoft 365 or enterprise outlook account mailbox, supporting filters for sender, subject, attachments, pagination, and sorting by relevance or date.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_GET_EVENT: {
    key: 'event.get',
    description:
      "Retrieves the full details of a specific calendar event by its id from a user's outlook calendar, provided the event exists.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_LIST_EVENTS: {
    key: 'event.list',
    description:
      "Retrieves events from a user's outlook calendar via microsoft graph api, supporting pagination, filtering, property selection, sorting, and timezone specification.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_GET_SCHEDULE: {
    key: 'schedule.get',
    description:
      'Retrieves free/busy schedule information for specified email addresses within a defined time window.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  OUTLOOK_OUTLOOK_CREATE_CONTACT: {
    key: 'contact.create',
    description: "Creates a new contact in a microsoft outlook user's contacts folder.",
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  OUTLOOK_OUTLOOK_UPDATE_CONTACT: {
    key: 'contact.update',
    description:
      'Updates an existing outlook contact, identified by `contact id` for the specified `user id`, requiring at least one other field to be modified.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  OUTLOOK_OUTLOOK_SEND_EMAIL: {
    key: 'message.send',
    description:
      'Sends an email with subject, body, recipients, and an optional attachment via microsoft graph api; attachments require a non-empty file with valid name and mimetype.',
    // A sent email cannot be unsent.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
  OUTLOOK_OUTLOOK_REPLY_EMAIL: {
    key: 'message.reply',
    description:
      'Sends a plain text reply to an outlook email message, identified by `message id`, allowing optional cc and bcc recipients.',
    // A sent reply cannot be unsent.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
  OUTLOOK_OUTLOOK_CREATE_DRAFT: {
    key: 'message.create_draft',
    description:
      'Creates an outlook email draft with subject, body, recipients, and an optional attachment. supports creating drafts as part of existing conversation threads by specifying a conversationid; attachments require a name, mimetype, and content.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  OUTLOOK_OUTLOOK_CREATE_DRAFT_REPLY: {
    key: 'message.create_draft_reply',
    description:
      "Creates a draft reply in the specified user's outlook mailbox to an existing message (identified by a valid `message id`), optionally including a `comment` and cc/bcc recipients.",
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  OUTLOOK_OUTLOOK_CALENDAR_CREATE_EVENT: {
    key: 'event.create',
    description:
      'Creates a new outlook calendar event, ensuring `start datetime` is chronologically before `end datetime`.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  OUTLOOK_OUTLOOK_UPDATE_CALENDAR_EVENT: {
    key: 'event.update',
    description: 'Updates specified fields of an existing outlook calendar event.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
};

// Every scope is in toolkit-outlook.json's registered default list; ReadWrite variants replace
// their Read-only counterparts (Graph scopes are hierarchical), so both are not requested.
const OUTLOOK_REQUIRED_SCOPES = [
  'Contacts.ReadWrite',
  'Mail.ReadWrite',
  'Mail.Send',
  'Calendars.ReadWrite',
  'Calendars.ReadWrite.Shared',
  'offline_access',
] as const;

// Notion: 9 reads, 6 reversible writes. Archive/delete, database-schema, and title-search tools
// are excluded: title-search duplicates FETCH_DATA, schema edits restructure the workspace.
const NOTION_TOOL_SLUGS = [
  'NOTION_FETCH_DATA',
  'NOTION_FETCH_BLOCK_CONTENTS',
  'NOTION_FETCH_BLOCK_METADATA',
  'NOTION_FETCH_DATABASE',
  'NOTION_FETCH_ROW',
  'NOTION_QUERY_DATABASE',
  'NOTION_FETCH_COMMENTS',
  'NOTION_LIST_USERS',
  'NOTION_GET_ABOUT_USER',
  'NOTION_CREATE_NOTION_PAGE',
  'NOTION_INSERT_ROW_DATABASE',
  'NOTION_UPDATE_ROW_DATABASE',
  'NOTION_UPDATE_PAGE',
  'NOTION_CREATE_COMMENT',
  'NOTION_ADD_MULTIPLE_PAGE_CONTENT',
] as const;

// Descriptions are taken verbatim from the captured fixture, not invented. FETCH_DATA is kept
// over SEARCH_NOTION_PAGE (same capability): only FETCH_DATA has a captured MCP baseline.
const NOTION_TOOL_METADATA: Readonly<Record<string, CuratedConnectionToolMetadata>> = {
  NOTION_FETCH_DATA: {
    key: 'workspace_item.list',
    description:
      'Fetches notion items (pages and/or databases) from the notion workspace, use this to get minimal data about the items in the workspace with a query or list all items in the workspace with minimal data',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_FETCH_BLOCK_CONTENTS: {
    key: 'block_content.list',
    description:
      'Retrieves a paginated list of direct, first-level child block objects along with contents for a given parent notion block or page id; use block ids from the response for subsequent calls to access deeply nested content.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_FETCH_BLOCK_METADATA: {
    key: 'block.get',
    description:
      'Fetches metadata for a notion block (or page, as pages are blocks) using its valid uuid; if the block has children, use fetch block contents to fetch their contents.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_FETCH_DATABASE: {
    key: 'database.get',
    description:
      "Fetches a notion database's structural metadata (properties, title, etc.) via its `database id`, not the data entries; `database id` must reference an existing database.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_FETCH_ROW: {
    key: 'row.get',
    description:
      "Retrieves a notion database row's properties and metadata; use fetch block contents for page content blocks.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_QUERY_DATABASE: {
    key: 'row.search',
    description:
      'Queries a notion database for pages (rows), where rows are pages and columns are properties; ensure sort property names correspond to existing database properties.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_FETCH_COMMENTS: {
    key: 'comment.list',
    description: 'Fetches unresolved comments for a specified notion block or page id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_LIST_USERS: {
    key: 'user.list',
    description:
      'Retrieves a paginated list of users (excluding guests) from the notion workspace; the number of users returned per page may be less than the requested `page size`.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_GET_ABOUT_USER: {
    key: 'user.get',
    description:
      'Retrieves detailed information about a specific notion user, such as their name, avatar, and email, based on their unique user id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  NOTION_CREATE_NOTION_PAGE: {
    key: 'page.create',
    description: 'Creates a new empty page in a notion workspace.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  NOTION_INSERT_ROW_DATABASE: {
    key: 'row.create',
    description: 'Creates a new page (row) in a specified notion database.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  NOTION_UPDATE_ROW_DATABASE: {
    key: 'row.update',
    description:
      'Updates or archives an existing notion database row (page) using its `row id`, allowing modification of its icon, cover, and/or properties; ensure the target page is accessible and property details (names/ids and values) align with the database schema and specified formats.',
    // The delete_row field can soft-delete via update; needs_approval plus Notion trash bounds it.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  NOTION_UPDATE_PAGE: {
    key: 'page.update',
    description:
      'Tool to update the properties, icon, cover, or archive status of a page. use when you need to modify existing page attributes.',
    // The archived field can soft-delete via update; needs_approval plus Notion trash bounds it.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  NOTION_CREATE_COMMENT: {
    key: 'comment.create',
    description:
      'Adds a comment to a notion page (via `parent page id`) or to an existing discussion thread (via `discussion id`); cannot create new discussion threads on specific blocks (inline comments).',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  NOTION_ADD_MULTIPLE_PAGE_CONTENT: {
    key: 'page_content.append',
    description:
      "Efficiently adds multiple standard content blocks to a notion page in a single api call with automatic markdown parsing. the 'content' field in notionrichtext blocks now automatically detects and parses markdown formatting including headers (# ## ###), bold (**text**), italic (*text*), strikethrough (~~text~~), inline code (`code`), links ([text](url)), and more. ideal for bulk content creation, ai agents, and replacing multiple individual add page content calls. supports automatic text formatting, content splitting, and up to 100 blocks per request.",
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
};

// toolkit-notion.json registers zero scopes (default: "") and the auth config itself confirms
// scopes: []; Notion capabilities are configured on the integration in Notion's own portal.
const NOTION_REQUIRED_SCOPES: readonly string[] = [];

// Asana: 18 reads, 5 reversible writes. Goals, portfolios, allocations, batch requests, and
// project sharing/ownership updates are excluded from a single-record loop.
const ASANA_TOOL_SLUGS = [
  'ASANA_GET_TYPEAHEAD_OBJECTS',
  'ASANA_GET_USER',
  'ASANA_GET_MULTIPLE_USERS',
  'ASANA_GET_MULTIPLE_WORKSPACES',
  'ASANA_GET_WORKSPACE',
  'ASANA_GET_A_PROJECT',
  'ASANA_GET_MULTIPLE_PROJECTS',
  'ASANA_GET_A_TASK',
  'ASANA_GET_MULTIPLE_TASKS',
  'ASANA_GET_STORIES_FOR_TASK',
  'ASANA_GET_SECTIONS_IN_PROJECT',
  'ASANA_CREATE_A_TASK',
  'ASANA_UPDATE_A_TASK',
  'ASANA_CREATE_TASK_COMMENT',
  'ASANA_ADD_TASK_TO_SECTION',
  'ASANA_CREATE_A_PROJECT',
  'ASANA_GET_CURRENT_USER',
  'ASANA_GET_WORKSPACE_PROJECTS',
  'ASANA_GET_USERS_FOR_WORKSPACE',
  'ASANA_GET_TEAMS_IN_WORKSPACE',
  'ASANA_GET_TAGS',
  'ASANA_GET_CUSTOM_FIELDS_FOR_WORKSPACE',
  'ASANA_GET_A_USER_TASK_LIST',
] as const;

// Descriptions are taken verbatim from the captured fixture, not invented. GET_MULTIPLE_TASKS
// and GET_MULTIPLE_USERS are kept over their narrower project/workspace-scoped near-duplicates.
const ASANA_TOOL_METADATA: Readonly<Record<string, CuratedConnectionToolMetadata>> = {
  ASANA_GET_TYPEAHEAD_OBJECTS: {
    key: 'object.search',
    description:
      'Tool to retrieve objects in a workspace via a typeahead search algorithm. use when you need to quickly find objects like tasks, projects, users, etc., based on a search string. this is useful for implementing auto-completion features.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_USER: {
    key: 'user.get',
    description: 'Get a user by their id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_MULTIPLE_USERS: {
    key: 'user.list',
    description:
      'Returns a list of users in an asana workspace or organization, optionally filtered by workspace or team gid, with support for pagination and specifying optional fields.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_MULTIPLE_WORKSPACES: {
    key: 'workspace.list',
    description:
      'Retrieves all workspaces accessible by the authenticated user, returning an empty list if the user has no accessible workspaces.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_WORKSPACE: {
    key: 'workspace.get',
    description:
      'Tool to retrieve details of a specific workspace by its gid. use when you need to get information about a particular asana workspace.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_A_PROJECT: {
    key: 'project.get',
    description:
      'Retrieves a specific asana project by its `project gid`, with an option to include additional fields for comprehensive details using `opt fields`; this action does not return tasks within the project.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_MULTIPLE_PROJECTS: {
    key: 'project.list',
    description:
      'Returns a list of projects, optionally filtered by workspace, team, or archived status, supporting pagination for large datasets.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_A_TASK: {
    key: 'task.get',
    description:
      'Retrieves full details for a specified task gid accessible by the user; use `opt fields` to customize returned data.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_MULTIPLE_TASKS: {
    key: 'task.list',
    description:
      'Retrieves a list of tasks, allowing filtering by assignee (requires `workspace`), project, section, `completed since`, and `modified since`; `workspace` also requires `assignee`.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_STORIES_FOR_TASK: {
    key: 'task_story.list',
    description:
      'Tool to get stories (comments, status updates, etc.) for a task. use when you need to retrieve the history or discussion associated with a specific task.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_SECTIONS_IN_PROJECT: {
    key: 'section.list',
    description:
      'Returns compact records for all sections (used to group tasks) in a specified project.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_CREATE_A_TASK: {
    key: 'task.create',
    description:
      "Creates a new asana task; requires 'workspace', 'parent', or 'projects' for association, and 'followers', 'projects', 'tags' are set only at creation.",
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  ASANA_UPDATE_A_TASK: {
    key: 'task.update',
    description: 'Updates attributes of an existing asana task identified by its task gid.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  ASANA_CREATE_TASK_COMMENT: {
    key: 'task_comment.create',
    description:
      'Adds a new text comment (story) to an existing asana task, appearing in its activity feed.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  ASANA_ADD_TASK_TO_SECTION: {
    key: 'task.move_to_section',
    description:
      'Adds an existing task to a section, optionally positioning it before or after another task in that section; if no position is specified, the task is added to the end.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  ASANA_CREATE_A_PROJECT: {
    key: 'project.create',
    description:
      'Creates a new asana project, requiring either a `workspace` or `team` gid for association, and returns the full project details.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  ASANA_GET_CURRENT_USER: {
    key: 'current_user.get',
    description:
      "Retrieves the authenticated user's full record, including accessible workspaces, often used as an initial call to establish user context for subsequent operations.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_WORKSPACE_PROJECTS: {
    key: 'workspace_project.list',
    description:
      'Tool to retrieve the projects associated with a specific workspace. use when you need to list all projects within a given asana workspace.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_USERS_FOR_WORKSPACE: {
    key: 'workspace_user.list',
    description: 'Get users in a workspace or organization.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_TEAMS_IN_WORKSPACE: {
    key: 'team.list',
    description:
      'Returns the compact records for all teams in the workspace visible to the authorized user.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_TAGS: {
    key: 'tag.list',
    description: 'Get multiple tags in a workspace.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_CUSTOM_FIELDS_FOR_WORKSPACE: {
    key: 'custom_field.list',
    description:
      'Tool to get all custom fields in a workspace. use when you need to retrieve a list of custom fields associated with a specific workspace.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  ASANA_GET_A_USER_TASK_LIST: {
    key: 'user_task_list.get',
    description:
      "Retrieves a specific user's task list from asana by its `user task list gid`, optionally returning extended details like name, owner, and workspace if specified in `opt fields`.",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
};

// toolkit-asana.json registers zero scopes (default: ""); Asana falls back to its all-access
// 'default' scope, so a granular list here would restrict nothing and risks drifting from reality.
const ASANA_REQUIRED_SCOPES: readonly string[] = [];

// Slack: 12 reads, 2 reversible writes. Every slug is the non-deprecated spelling; invite, join,
// and channel-create are excluded: each widens access with no reverse in the curated set.
const SLACK_TOOL_SLUGS = [
  'SLACK_FIND_USERS',
  'SLACK_RETRIEVE_DETAILED_USER_INFORMATION',
  'SLACK_LIST_ALL_USERS',
  'SLACK_FIND_CHANNELS',
  'SLACK_LIST_ALL_CHANNELS',
  'SLACK_RETRIEVE_CONVERSATION_INFORMATION',
  'SLACK_RETRIEVE_CONVERSATION_MEMBERS_LIST',
  'SLACK_FETCH_CONVERSATION_HISTORY',
  'SLACK_FETCH_MESSAGE_THREAD_FROM_A_CONVERSATION',
  'SLACK_SEARCH_MESSAGES',
  'SLACK_SEND_MESSAGE',
  'SLACK_UPDATES_A_SLACK_MESSAGE',
  'SLACK_OPEN_DM',
  'SLACK_LIST_CONVERSATIONS',
] as const;

// Descriptions are taken verbatim from the captured fixture, not invented. Every write tool
// left is a message send or edit; channel-create and invite carry risk with no reverse tool.
const SLACK_TOOL_METADATA: Readonly<Record<string, CuratedConnectionToolMetadata>> = {
  SLACK_FIND_USERS: {
    key: 'user.search',
    description:
      'Find users in a slack workspace by any criteria - email, name, display name, or other text. includes optimized email lookup for exact email matches.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_RETRIEVE_DETAILED_USER_INFORMATION: {
    key: 'user.get',
    description:
      'Retrieves comprehensive information for a valid slack user id, excluding message history and channel memberships.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_LIST_ALL_USERS: {
    key: 'user.list',
    description:
      'Retrieves a paginated list of all users, including comprehensive details, profile information, status, and team memberships, in a slack workspace; data may not be real-time.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_FIND_CHANNELS: {
    key: 'channel.search',
    description:
      'Find channels in a slack workspace by any criteria - name, topic, purpose, or description.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_LIST_ALL_CHANNELS: {
    key: 'channel.list',
    description:
      'Lists conversations available to the user with various filters and search options.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_RETRIEVE_CONVERSATION_INFORMATION: {
    key: 'channel.get',
    description:
      'Retrieves metadata for a slack conversation by id (e.g., name, purpose, creation date, with options for member count/locale), excluding message content; requires a valid channel id.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_RETRIEVE_CONVERSATION_MEMBERS_LIST: {
    key: 'channel_member.list',
    description:
      'Retrieves a paginated list of active member ids for a specified slack public channel, private channel, direct message (dm), or multi-person direct message (mpim).',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_FETCH_CONVERSATION_HISTORY: {
    key: 'message.list',
    description:
      'Fetches a chronological list of messages and events from a specified slack conversation, accessible by the authenticated user/bot, with options for pagination and time range filtering.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_FETCH_MESSAGE_THREAD_FROM_A_CONVERSATION: {
    key: 'message_thread.list',
    description:
      "Retrieves replies to a specific parent message in a slack conversation, using the channel id and the parent message's timestamp (`ts`).",
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_SEARCH_MESSAGES: {
    key: 'message.search',
    description:
      'Workspace‑wide slack message search with date ranges and filters. use `query` modifiers (e.g., in:#channel, from:@user, before/after:yyyy-mm-dd), sorting (score/timestamp), and pagination.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_SEND_MESSAGE: {
    key: 'message.send',
    description:
      'Posts a message to a slack channel, direct message, or private group; requires content via `text`, `blocks`, or `attachments`.',
    // A posted message cannot be unsent, only edited.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
  SLACK_UPDATES_A_SLACK_MESSAGE: {
    key: 'message.update',
    description:
      'Updates a slack message, identified by `channel` id and `ts` timestamp, by modifying its `text`, `attachments`, or `blocks`; provide at least one content field, noting `attachments`/`blocks` are replaced if included (`[]` clears them).',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SLACK_OPEN_DM: {
    key: 'dm.open',
    description:
      'Opens or resumes a slack direct message (dm) or multi-person direct message (mpim) by providing either user ids or an existing channel id.',
    // Opening a DM posts nothing and is idempotent; the send that follows still needs approval.
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SLACK_LIST_CONVERSATIONS: {
    key: 'conversation.list',
    description:
      'Retrieves conversations accessible to a specified user (or the authenticated user if no user id is provided), respecting shared membership for non-public channels.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
};

// Every scope is in toolkit-slack.json's registered default list. conversations:read is not a
// real Slack scope: the four channels/groups/im/mpim reads above already cover conversations.
const SLACK_REQUIRED_SCOPES = [
  'users:read',
  'users:read.email',
  'channels:read',
  'groups:read',
  'mpim:read',
  'im:read',
  'channels:history',
  'groups:history',
  'im:history',
  'mpim:history',
  'search:read',
  'chat:write',
  // dm.open (SLACK_OPEN_DM) opens DMs and group DMs.
  'im:write',
  'mpim:write',
] as const;

// Salesforce: 8 reads, 11 reversible writes across account/contact/lead/opportunity. Campaign,
// report, dashboard, mass-email, and unbounded SOQL-list tools are excluded (see scope comment).
const SALESFORCE_TOOL_SLUGS = [
  'SALESFORCE_SEARCH_ACCOUNTS',
  'SALESFORCE_GET_ACCOUNT',
  'SALESFORCE_CREATE_ACCOUNT',
  'SALESFORCE_UPDATE_ACCOUNT',
  'SALESFORCE_SEARCH_CONTACTS',
  'SALESFORCE_GET_CONTACT',
  'SALESFORCE_CREATE_CONTACT',
  'SALESFORCE_UPDATE_CONTACT',
  'SALESFORCE_SEARCH_LEADS',
  'SALESFORCE_GET_LEAD',
  'SALESFORCE_CREATE_LEAD',
  'SALESFORCE_UPDATE_LEAD',
  'SALESFORCE_SEARCH_OPPORTUNITIES',
  'SALESFORCE_GET_OPPORTUNITY',
  'SALESFORCE_CREATE_OPPORTUNITY',
  'SALESFORCE_UPDATE_OPPORTUNITY',
  'SALESFORCE_CREATE_NOTE',
  'SALESFORCE_SEND_EMAIL',
  'SALESFORCE_ASSOCIATE_CONTACT_TO_ACCOUNT',
] as const;

// Descriptions are taken verbatim from the captured fixture, not invented. LIST_ACCOUNTS and
// LIST_CONTACTS took a free-text SOQL query with no FROM-clause constraint; cut, see below.
const SALESFORCE_TOOL_METADATA: Readonly<Record<string, CuratedConnectionToolMetadata>> = {
  SALESFORCE_SEARCH_ACCOUNTS: {
    key: 'account.search',
    description:
      'Search for salesforce accounts using multiple criteria like name, industry, type, location, or contact information.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_GET_ACCOUNT: {
    key: 'account.get',
    description:
      'Retrieves a specific account by id from salesforce, returning all available fields.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_CREATE_ACCOUNT: {
    key: 'account.create',
    description: 'Creates a new account in salesforce with the specified information.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_UPDATE_ACCOUNT: {
    key: 'account.update',
    description:
      'Updates an existing account in salesforce with the specified changes. only provided fields will be updated.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_SEARCH_CONTACTS: {
    key: 'contact.search',
    description:
      'Search for salesforce contacts using multiple criteria like name, email, phone, account, or title.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_GET_CONTACT: {
    key: 'contact.get',
    description:
      'Retrieves a specific contact by id from salesforce, returning all available fields.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_CREATE_CONTACT: {
    key: 'contact.create',
    description: 'Creates a new contact in salesforce with the specified information.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_UPDATE_CONTACT: {
    key: 'contact.update',
    description:
      'Updates an existing contact in salesforce with the specified changes. only provided fields will be updated.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_SEARCH_LEADS: {
    key: 'lead.search',
    description:
      'Search for salesforce leads using multiple criteria like name, email, phone, company, title, status, or lead source.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_GET_LEAD: {
    key: 'lead.get',
    description: 'Retrieves a specific lead by id from salesforce, returning all available fields.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_CREATE_LEAD: {
    key: 'lead.create',
    description: 'Creates a new lead in salesforce with the specified information.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_UPDATE_LEAD: {
    key: 'lead.update',
    description:
      'Updates an existing lead in salesforce with the specified changes. only provided fields will be updated.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_SEARCH_OPPORTUNITIES: {
    key: 'opportunity.search',
    description:
      'Search for salesforce opportunities using multiple criteria like name, account, stage, amount, close date, or status.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_GET_OPPORTUNITY: {
    key: 'opportunity.get',
    description:
      'Retrieves a specific opportunity by id from salesforce, returning all available fields.',
    effect: 'read',
    defaultPolicy: 'always_allow',
    registryFloor: 'always_allow',
    idempotency: 'safe_retry',
  },
  SALESFORCE_CREATE_OPPORTUNITY: {
    key: 'opportunity.create',
    description: 'Creates a new opportunity in salesforce with the specified information.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_UPDATE_OPPORTUNITY: {
    key: 'opportunity.update',
    description:
      'Updates an existing opportunity in salesforce with the specified changes. only provided fields will be updated.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_CREATE_NOTE: {
    key: 'note.create',
    description:
      'Creates a new note attached to a salesforce record with the specified title and content.',
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'keyed',
  },
  SALESFORCE_SEND_EMAIL: {
    key: 'email.send',
    description:
      'Sends an email through salesforce with options for recipients, attachments, and activity logging.',
    // A sent email cannot be unsent.
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
  SALESFORCE_ASSOCIATE_CONTACT_TO_ACCOUNT: {
    key: 'contact.associate_account',
    description: "Associates a contact with an account by updating the contact's accountid field.",
    effect: 'reversible_write',
    defaultPolicy: 'needs_approval',
    registryFloor: 'needs_approval',
    idempotency: 'never_retry_unknown',
  },
};

// toolkit-salesforce.json registers exactly full, refresh_token, openid: no narrower option exists.
// This is zero OAuth-level restriction; needs_approval on every write is the only real gate here.
const SALESFORCE_REQUIRED_SCOPES = ['full', 'refresh_token'] as const;

// Rendered verbatim in the connector detail's Details section. Plain language, no marketing: the
// grant is total, Salesforce offers nothing narrower, and the approval floor cannot be disabled.
const SALESFORCE_ACCESS_NOTE =
  'Connecting Salesforce grants full read and write access to everything the connecting user can see in Salesforce. Salesforce does not offer a narrower scope through this integration. Every Salesforce write still requires explicit approval in Parcel, and that requirement cannot be turned off.';

function buildCatalog(): readonly ConnectionCatalogEntry[] {
  return [
    buildCatalogEntry({
      key: 'hubspot',
      name: 'HubSpot',
      description: 'Search and manage HubSpot contacts, companies, and deals.',
      icon: 'hubspot',
      categories: ['crm', 'sales_customer_support'],
      providerToolkit: 'hubspot',
      scopingModes: ['workspace', 'member'],
      sharedPolicyFloor: 'needs_approval',
      requiredScopes: HUBSPOT_REQUIRED_SCOPES,
      toolSlugs: HUBSPOT_TOOL_SLUGS,
      toolMetadata: HUBSPOT_TOOL_METADATA,
    }),
    buildCatalogEntry({
      key: 'microsoft_teams',
      name: 'Microsoft Teams',
      description: 'Read and post Microsoft Teams channel messages and chats.',
      icon: 'microsoft_teams',
      categories: ['collaboration_communication'],
      providerToolkit: 'microsoft_teams',
      scopingModes: ['member'],
      sharedPolicyFloor: 'needs_approval',
      requiredScopes: MICROSOFT_TEAMS_REQUIRED_SCOPES,
      toolSlugs: MICROSOFT_TEAMS_TOOL_SLUGS,
      toolMetadata: MICROSOFT_TEAMS_TOOL_METADATA,
    }),
    buildCatalogEntry({
      key: 'outlook',
      name: 'Outlook',
      description: 'Search Outlook mail and calendar, and manage contacts and events.',
      icon: 'outlook',
      categories: ['collaboration_communication', 'scheduling_booking'],
      providerToolkit: 'outlook',
      scopingModes: ['member'],
      sharedPolicyFloor: 'needs_approval',
      requiredScopes: OUTLOOK_REQUIRED_SCOPES,
      toolSlugs: OUTLOOK_TOOL_SLUGS,
      toolMetadata: OUTLOOK_TOOL_METADATA,
    }),
    buildCatalogEntry({
      key: 'notion',
      name: 'Notion',
      description: 'Search and manage Notion pages, database rows, and comments.',
      icon: 'notion',
      categories: ['document_file_management', 'productivity_project_management'],
      providerToolkit: 'notion',
      scopingModes: ['workspace', 'member'],
      sharedPolicyFloor: 'needs_approval',
      requiredScopes: NOTION_REQUIRED_SCOPES,
      toolSlugs: NOTION_TOOL_SLUGS,
      toolMetadata: NOTION_TOOL_METADATA,
    }),
    buildCatalogEntry({
      key: 'asana',
      name: 'Asana',
      description: 'Search and manage Asana tasks, projects, and task comments.',
      icon: 'asana',
      categories: ['productivity_project_management'],
      providerToolkit: 'asana',
      scopingModes: ['workspace', 'member'],
      sharedPolicyFloor: 'needs_approval',
      requiredScopes: ASANA_REQUIRED_SCOPES,
      toolSlugs: ASANA_TOOL_SLUGS,
      toolMetadata: ASANA_TOOL_METADATA,
    }),
    buildCatalogEntry({
      key: 'slack',
      name: 'Slack',
      description: 'Search Slack channels and messages, and post or update messages.',
      icon: 'slack',
      categories: ['collaboration_communication'],
      providerToolkit: 'slack',
      scopingModes: ['workspace', 'member'],
      sharedPolicyFloor: 'needs_approval',
      requiredScopes: SLACK_REQUIRED_SCOPES,
      toolSlugs: SLACK_TOOL_SLUGS,
      toolMetadata: SLACK_TOOL_METADATA,
    }),
    buildCatalogEntry({
      key: 'salesforce',
      name: 'Salesforce',
      description: 'Search and manage Salesforce accounts, contacts, leads, and opportunities.',
      icon: 'salesforce',
      categories: ['crm', 'sales_customer_support'],
      providerToolkit: 'salesforce',
      scopingModes: ['workspace', 'member'],
      sharedPolicyFloor: 'needs_approval',
      requiredScopes: SALESFORCE_REQUIRED_SCOPES,
      accessNote: SALESFORCE_ACCESS_NOTE,
      toolSlugs: SALESFORCE_TOOL_SLUGS,
      toolMetadata: SALESFORCE_TOOL_METADATA,
    }),
  ];
}

export const CONNECTION_CATALOG: readonly ConnectionCatalogEntry[] = buildCatalog();

// A Map, not an object: a bare object lookup would resolve "__proto__" and
// "constructor" to inherited values and hand back a truthy non-entry.
const CATALOG_BY_KEY = new Map(CONNECTION_CATALOG.map((entry) => [entry.key, entry]));

export function getCatalogEntry(key: string): ConnectionCatalogEntry | null {
  return CATALOG_BY_KEY.get(key) ?? null;
}

export function getCuratedTool(
  catalogKey: string,
  providerSlug: string,
): CuratedConnectionTool | null {
  const entry = getCatalogEntry(catalogKey);
  if (!entry) return null;
  return entry.tools.find((tool) => tool.providerSlug === providerSlug) ?? null;
}
